import { KeyboardEvent } from 'react';
import styled from 'styled-components';
import noop from 'lodash/noop';

import { SPACE_KEY } from 'constants/keyCodes';
import { colors, easing, duration, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

type StyledRadioButtonProps = { isDisabled: boolean; size: number };
const StyledRadioButton = styled.svg<StyledRadioButtonProps>`
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};
  &:hover {
    cursor: pointer;
  }
  display: inline-block;
  height: auto;
  width: ${({ size }: { size: number }) => pxToRem(size)};
  outline-style: none;

  .radio-hover-back,
  .radio-hover-mid,
  .radio-middle {
    fill: ${colors.white};
    transition: all ${duration.short} ${easing.entrance};
  }

  &:not([aria-checked='true']) {
    .radio-back,
    .radio-front {
      fill: ${colors.grey25};
    }
    &:focus,
    &:hover .radio-back {
      fill: ${colors.grey35};
    }
  }

  &[aria-checked='true'] {
    .radio-back {
      fill: ${colors.green100};
    }
    &:hover,
    &:focus .radio-back {
      outline: solid ${spacing.single} ${colors.white};
      border-radius: ${spacing.x48};
    }
    .radio-middle {
      fill: ${colors.green100};
    }
    .radio-front {
      fill: ${colors.white};
    }
    &:not([aria-disabled='true']) {
      &:hover .radio-hover-back {
        fill: ${colors.green20};
        r: 11;
      }
      &:focus .radio-hover-back {
        fill: ${colors.green50};
        outline-style: none;
        r: 12;
      }
    }
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    .radio-back,
    .radio-middle {
      fill: ${colors.grey35};
    }
    .radio-front {
      fill: ${colors.white};
    }
    &:not([aria-checked='true']) {
      .radio-front,
      .radio-middle {
        fill: ${colors.grey10};
      }
    }
  }

  &:not([aria-disabled='true']) {
    &:focus .radio-hover-back {
      fill: ${colors.grey15};
      outline-style: none;
      r: 11;
    }
  }
`;

export const getRadioButtonTestId = (id = '') => `radio-button-${id}`;

export type RadioProps = {
  /** unique id for the radio button */
  id?: string;
  /** Whether the command or control is checked */
  isChecked?: boolean;
  /** Whether the command or control is disabled */
  isDisabled?: boolean;
  /** Name of the input form control. Submitted with the form as part of a name/value pair. */
  name?: string;
  /** Function ran when the input is clicked */
  onClick?: () => void;
  /** If controlled by RadioList, onKeyDown should control */
  onKeyDown?: (e: KeyboardEvent) => void;
  /** how wide the button in PX  */
  size?: number;
  /** In context of a RadioList, tabIndex is controlled by parent */
  tabIndex?: number;
  /** Current value of the form control */
  value: any;
};

export const Radio = ({
  isChecked = false,
  isDisabled = false,
  id,
  name,
  onClick = noop,
  onKeyDown,
  size = 24,
  tabIndex = 0,
  value,
  ...props
}: RadioProps) => {
  const handleClick = isDisabled ? undefined : onClick;
  const handleKeyPress = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.keyCode === SPACE_KEY) {
      onClick();
    }
  };
  const handleKeyDown = onKeyDown || handleKeyPress;

  // radio buttons need extension for proper keyboard support
  // https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/radio/radio.html
  return (
    <StyledRadioButton
      id={id}
      aria-checked={isChecked}
      aria-disabled={isDisabled}
      data-test={getRadioButtonTestId(id)}
      name={name}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="radio"
      size={size}
      tabIndex={tabIndex}
      // @ts-ignore
      value={value}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle className="radio-hover-back" cx="12" cy="12" r="6" />
      <circle className="radio-hover-mid" cx="12" cy="12" r="9" />
      <circle className="radio-back" cx="12" cy="12" r="9" />
      <circle className="radio-middle" cx="12" cy="12" r="8" />
      <circle className="radio-front" cx="12" cy="12" r={isChecked ? 3 : 0} />
    </StyledRadioButton>
  );
};
